import React, { useContext, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone } from "../controller/global";
import { submitLead } from "../controller/leads";
import { Images } from "../controller/Images";
import { FaBolt, FaChartLine, FaChevronLeft, FaChevronRight, FaDiagramProject, FaGears, FaPlug, FaRobot } from "react-icons/fa6";

const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

export const agenticAISEO = {
  // The page ranked ~86 for "ai agent integration services" while using the
  // phrase "AI agent" zero times — it was written entirely in "agentic AI"
  // vocabulary. Both terms are standard; the copy now uses both. The old title
  // also ran to 79 characters and was truncated in results.
  title: "Agentic AI Development & AI Agent Integration | InQmise",
  description:
    "We build and integrate AI agents into the systems you already run — agents that decide and act, with human oversight and guardrails from day one.",
  keywords:
    "agentic AI development company, autonomous agent systems, AI agents development, enterprise AI solutions, intelligent automation, custom AI solutions, multi-agent systems, autonomous workflows, AI automation services, agent-based AI development",
  canonical: "https://inqmisetechnologies.com/agentic-ai-development",
  og: {
    title: "Agentic AI Development & AI Agent Integration",
    description: "AI agents that decide and act inside your existing systems, with guardrails and human oversight from day one.",
    url: "https://inqmisetechnologies.com/agentic-ai-development",
    type: "website",
    image: "https://inqmisetechnologies.com/logo.png",
  },
  twitter: {
    card: "summary_large_image",
    title: "Agentic AI Development & AI Agent Integration",
    description: "AI agents that decide and act inside your existing systems, with guardrails and human oversight from day one.",
    image: "https://inqmisetechnologies.com/logo.png",
  },
};

const SYSTEMS = [
  {
    num: "01 / PERCEIVE",
    title: "It reads the situation, not a report.",
    copy: "Agents watch your data and systems as things happen, so a decision is never made on stale information.",
    tags: ["Live data streams", "System-of-record awareness", "Context over snapshots"],
  },
  {
    num: "02 / DECIDE & ACT",
    title: "Judgment calls, executed without waiting.",
    copy: "Given a goal, the agent works out the right steps and carries them out — flagging you only when it truly matters.",
    tags: ["Goal-directed planning", "Human approval checkpoints", "Tool & API execution"],
  },
  {
    num: "03 / IMPROVE",
    title: "Every task teaches it something.",
    copy: "Outcomes feed back into the system, so the agent you have in six months makes better calls than the one you have today.",
    tags: ["Outcome feedback loops", "Behavior tuning", "Audit trail"],
  },
];

const PROCESS_STEPS = [
  { n: "01", t: "Discovery & strategy", d: "Understand the workflows and decisions where autonomy actually creates value." },
  { n: "02", t: "Agent architecture", d: "Design decision logic, memory, and collaboration patterns before writing production code." },
  { n: "03", t: "Build & integrate", d: "Develop against your real systems and APIs, with testing and safety checks built in from day one." },
  { n: "04", t: "Deploy & optimize", d: "Ship to production with monitoring in place, then tune behavior against real outcomes." },
];

const SERVICES = [
  { Icon: FaRobot, title: "Autonomous agent systems", copy: "Agents that make decisions and complete tasks on their own, without approval needed for every step.", featured: true },
  { Icon: FaDiagramProject, title: "Multi-agent orchestration", copy: "Several agents working as one team — one checks orders, another handles support, another manages stock." },
  { Icon: FaGears, title: "Intelligent automation", copy: "Multi-step manual work — approvals, data entry, follow-ups — handled correctly and on time." },
  { Icon: FaPlug, title: "AI integration services", copy: "Plugs into the tools you already run — CRM, email, ERP — no need to replace anything." },
  { Icon: FaChartLine, title: "Real-time learning", copy: "Agents get sharper with every interaction, without a manual retraining cycle." },
  { Icon: FaBolt, title: "Custom AI solutions", copy: "Built around how your business actually operates, not a generic template that forces you to change process." },
];

// true = full support, "partial" = partial support, false = not supported.
const COMPARE_ROWS = [
  { label: "Autonomous decision-making", trad: false, generic: "partial" },
  { label: "Continuous learning & adaptation", trad: false, generic: "partial" },
  { label: "Human oversight & control", trad: true, generic: false },
  { label: "Custom enterprise integration", trad: "partial", generic: false },
  { label: "Enterprise-grade security & compliance", trad: "partial", generic: "partial" },
  { label: "Ongoing optimization & support", trad: false, generic: false },
];

const EVAL_METRICS = [
  { a: "8+ yrs", b: "building production AI systems" },
  { a: "50+", b: "AI deployments shipped" },
  { a: "40–60%", b: "less manual work in the first quarter" },
  { a: "24/7", b: "autonomous operation, human oversight intact" },
];

const CASE_RESULTS = [
  { a: "80–90%", b: "recommendation accuracy in production" },
  { a: "24/7", b: "autonomous personalization, no manual curation" },
  { a: "0", b: "disruption to the existing platform" },
  { a: "100%", b: "decisions logged and auditable" },
];

const INDUSTRIES = [
  { a: "E-commerce", b: "Agents handle customer inquiries around the clock and escalate when it matters." },
  { a: "Finance", b: "Autonomous agents monitor markets and execute decisions within defined limits." },
  { a: "Manufacturing", b: "Agents predict demand, optimize inventory, and coordinate logistics." },
  { a: "Healthcare", b: "Intelligent agents schedule, follow up, and monitor within strict guardrails." },
  { a: "HR & recruitment", b: "Agents screen candidates and coordinate scheduling, humans make the calls." },
  { a: "Marketing", b: "Agents optimize spend and personalize journeys against real performance data." },
];

const FAQS = [
  {
    q: "What is agentic AI development?",
    a: "Building autonomous agent systems that can make decisions and take actions independently — within limits you define — rather than waiting on step-by-step human instruction for every task.",
  },
  {
    q: "How does agentic AI differ from traditional automation?",
    a: "Traditional automation follows a fixed flowchart — if this happens, do that — and breaks the moment something unexpected occurs. An agent reasons through the situation, decides on a course of action, and adapts as circumstances change.",
  },
  {
    q: "How long does it take to build an agentic AI system?",
    a: "Typically 8-16 weeks for a production-ready system depending on complexity. We can usually get a working pilot in front of you in 4-6 weeks.",
  },
  {
    q: "Is agentic AI safe and reliable for enterprise use?",
    a: "We build in safety guardrails, human approval checkpoints for critical decisions, full decision logging, and comprehensive testing before anything reaches production.",
  },
  {
    q: "Can agents integrate with our existing systems?",
    a: "Yes. We design agents to work with your current CRM, ERP, and internal APIs — integration is part of the architecture from the start, not an afterthought.",
  },
  {
    q: "What industries benefit from agentic AI?",
    a: "E-commerce, finance, manufacturing, healthcare, HR, and marketing all see measurable gains from agentic automation — anywhere repetitive decisions currently eat up a team's time.",
  },
  {
    q: "How much does agentic AI development cost?",
    a: "It depends on how many systems the agent works with, how many decisions it makes on its own and how much human review is needed. A single, narrow agent costs far less than a multi-agent system. We scope one workflow first and give a fixed estimate.",
  },
  {
    q: "What are AI agent development services?",
    a: "Designing, building and deploying AI agents that plan and complete tasks in your systems: reading data, calling APIs, making decisions within limits you set and handing off to people when they should. It includes the guardrails, logging and monitoring that make agents safe to run.",
  },
  {
    q: "How do you keep AI agents from taking the wrong action?",
    a: "Agents only get the permissions a task needs, risky actions need human approval, every action is logged, and we test agents against real scenarios before they touch production.",
  },
  {
    q: "Who owns the code and the AI system you build?",
    a: "You do. Source code, prompts, evaluation sets, trained model weights and documentation are handed over and belong to you, and we sign an NDA before any detailed discussion.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

const AgenticAIDevelopment = () => {
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();
  const servicesScrollRef = useRef(null);
  const scrollServices = (dir) => {
    servicesScrollRef.current?.scrollBy({ left: dir * 290, behavior: "smooth" });
  };

  useEffect(() => {
    document.title = agenticAISEO.title;

    const description = document.createElement("meta");
    description.name = "description";
    description.content = agenticAISEO.description;
    document.head.appendChild(description);

    const keywords = document.createElement("meta");
    keywords.name = "keywords";
    keywords.content = agenticAISEO.keywords;
    document.head.appendChild(keywords);

    const ogTitle = document.createElement("meta");
    ogTitle.setAttribute("property", "og:title");
    ogTitle.content = agenticAISEO.og.title;
    document.head.appendChild(ogTitle);

    const ogDesc = document.createElement("meta");
    ogDesc.setAttribute("property", "og:description");
    ogDesc.content = agenticAISEO.og.description;
    document.head.appendChild(ogDesc);

    const ogUrl = document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    ogUrl.content = agenticAISEO.og.url;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement("meta");
    ogType.setAttribute("property", "og:type");
    ogType.content = agenticAISEO.og.type;
    document.head.appendChild(ogType);

    const ogImage = document.createElement("meta");
    ogImage.setAttribute("property", "og:image");
    ogImage.content = agenticAISEO.og.image;
    document.head.appendChild(ogImage);

    const twitterCard = document.createElement("meta");
    twitterCard.name = "twitter:card";
    twitterCard.content = agenticAISEO.twitter.card;
    document.head.appendChild(twitterCard);

    const twitterTitle = document.createElement("meta");
    twitterTitle.name = "twitter:title";
    twitterTitle.content = agenticAISEO.twitter.title;
    document.head.appendChild(twitterTitle);

    const twitterDesc = document.createElement("meta");
    twitterDesc.name = "twitter:description";
    twitterDesc.content = agenticAISEO.twitter.description;
    document.head.appendChild(twitterDesc);

    const twitterImage = document.createElement("meta");
    twitterImage.name = "twitter:image";
    twitterImage.content = agenticAISEO.twitter.image;
    document.head.appendChild(twitterImage);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", agenticAISEO.canonical);

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Agentic AI Development",
      "description": agenticAISEO.description,
      "url": agenticAISEO.canonical,
      "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
      "availableLanguage": ["en-IN"],
      "serviceType": "Agentic AI Development",
    });
    document.head.appendChild(schemaScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
        { "@type": "ListItem", "position": 3, "name": "Agentic AI Development", "item": agenticAISEO.canonical },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Tell us a little about your project.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO — same layout order (text left, panel right) and same
          ea-engine panel shape (query rows + flow chip) as the other AI
          service pages */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">Autonomous AI systems · accountable by design</p>
          <h1>
            Agentic AI development that <em>makes the call</em> in your operations.
          </h1>
          <p className="ea-lede">
            An agentic AI development company in Delhi NCR, building AI agent integration for teams who need software that decides rather than reports. We build agentic AI systems that execute workflows and optimize outcomes in real time &mdash; inside the systems you already run, with human oversight at the center of every decision.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to plan your agent system">
              Plan your agent system ↗
            </button>
            <a className="ea-text-link" href="#systems">See how it works</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / AGENT ENGINE <b>LIVE</b></div>
          <h2>Should this refund request be auto-approved?</h2>
          <div className="ea-query"><small>PERCEIVE</small>Order history, return policy and customer standing pulled against current rules.</div>
          <div className="ea-query"><small>DECIDE</small>Request matches policy for auto-approval; one edge case flagged for review.</div>
          <div className="ea-flow"><b>Perceive</b><i>→</i><b>Decide</b><i>→</i><b>Act</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* TERMINOLOGY + WHAT MAKES AN AGENT DIFFERENT */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">First, the vocabulary</div>
              <h2>AI agents, agentic AI, agent integration &mdash; the same thing?</h2>
            </div>
          </Reveal>
          <Reveal as="div" i={1} reduceMotion={reduceMotion}>
            <div className="std-overview">
              <p>
                Broadly, yes, and the inconsistency is a genuine obstacle when you are trying to
                scope work. <strong>AI agent</strong> usually names the thing itself &mdash; software
                that decides and acts. <strong>Agentic AI</strong> names the wider approach.
                <strong> AI agent integration services</strong> is what you search for when the
                agent has to work inside systems you already run, which is almost always the real
                requirement. We use the terms interchangeably below, because in practice buyers do.
              </p>
              <p>
                The distinction that actually matters is not vocabulary. It is the line between a
                model that answers and a system that acts.
              </p>
              <p>
                <strong>An LLM call returns text. An agent changes something.</strong> Ask a model a
                question and the worst outcome is a wrong answer that a person reads and discards.
                Give an agent the ability to issue a refund, reroute a shipment or unlock a door,
                and a wrong decision has already happened by the time anyone reviews it. Everything
                difficult about agent work follows from that one difference.
              </p>
              <p>
                <strong>Which is why the interesting engineering is in the boundaries, not the
                reasoning.</strong> What is this agent permitted to do without asking? What is the
                monetary or blast-radius ceiling above which a human must approve? What does it do
                when it is uncertain &mdash; act, escalate, or stop? An agent without an explicit
                answer to those three questions is a prototype regardless of how well it reasons.
              </p>
              <p>
                <strong>And an agent that cannot explain itself cannot be trusted with anything that
                matters.</strong> When a decision is questioned weeks later, &ldquo;the model decided&rdquo; is
                not an answer anyone accepts. Each step an agent takes needs to be recorded with what
                it saw and why it chose what it chose &mdash; which is a logging and data-modeling
                problem, not an AI one.
              </p>
              <p>
                In integration terms this means an agent is rarely a component you drop in. It needs
                permission to act on systems that were built assuming a human was on the other end,
                and those systems generally have no concept of &ldquo;approved up to this limit&rdquo;. That
                gap is the work.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW AGENTS OPERATE */}
      <section className="ea-wrap ea-section" id="systems">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">The operating loop</div>
            <h2>Autonomy, made accountable.</h2>
          </div>
          <p>Not a chatbot with extra steps. Every stage exists to make a decision more useful, safer, or easier to improve.</p>
        </Reveal>

        <div className="aa-systems-grid">
          {SYSTEMS.map((item, i) => (
            <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="aa-system-card" key={item.title}>
              <span className="ea-num">{item.num}</span>
              <h3>{item.title}</h3>
              <p>{item.copy}</p>
              <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">A disciplined route to value</div>
            <h2>Prove it works<br />before it runs unattended.</h2>
          </div>
          <p>A four-part engagement designed to replace uncertainty with proof, then turn proof into an operating system.</p>
        </Reveal>
        <div className="aa-process-line">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-process-row" key={step.n}>
              <div className="aa-process-copy">
                <h3>{step.t}</h3>
                <p>{step.d}</p>
              </div>
              <div className="aa-process-node">{step.n}</div>
              <div />
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">What we build</div>
              <h2>Modular services.<br />One accountable system.</h2>
            </div>
            <p>Bring us in for a single agent or keep us close from the first pilot to full-scale orchestration.</p>
          </Reveal>
          <div className="aa-scroller" ref={servicesScrollRef}>
            {SERVICES.map((item, i) => (
              <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} key={item.title} className={`aa-scroll-card ${item.featured ? "ea-featured" : ""}`}>
                <div className="ea-icon"><item.Icon aria-hidden="true" /></div>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </Reveal>
            ))}
          </div>
          <div className="aa-scroll-nav">
            <button onClick={() => scrollServices(-1)} aria-label="Previous services" className="aa-scroll-btn">
              <FaChevronLeft className="text-xs" />
            </button>
            <button onClick={() => scrollServices(1)} aria-label="Next services" className="aa-scroll-btn">
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        </div>
      </section>

      {/* WHY THIS APPROACH — a real comparison table instead of the
          Generative AI page's pine-panel + metrics layout */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Built in, not bolted on</div>
            <h2>Autonomy with<br />guardrails from day one.</h2>
          </div>
          <p>Every agent we ship is checked against the same bar before it touches production.</p>
        </Reveal>

        <Reveal as="div" reduceMotion={reduceMotion} style={{ overflowX: "auto" }}>
          <table className="aa-compare">
            <thead>
              <tr>
                <th>Capability</th>
                <th className="aa-compare-us">InQmise agentic AI</th>
                <th>Traditional automation</th>
                <th>Generic AI tools</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td className="aa-compare-us"><span className="aa-check">✓</span></td>
                  <td className="aa-compare-center">
                    {row.trad === true ? <span className="aa-check">✓</span> : row.trad === "partial" ? <span className="aa-partial">~</span> : <span className="aa-cross">✕</span>}
                  </td>
                  <td className="aa-compare-center">
                    {row.generic === true ? <span className="aa-check">✓</span> : row.generic === "partial" ? <span className="aa-partial">~</span> : <span className="aa-cross">✕</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        <div className="aa-stats-strip">
          {EVAL_METRICS.map((m, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-stat" key={m.a}>
              <strong>{m.a}</strong>
              <span>{m.b}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CASE STUDY */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Selected outcome</div>
            <h2>Personalization that<br />runs without a curator.</h2>
          </div>
          <p>An AI-powered recommendation engine for a fashion retailer, deciding what to surface to each shopper without manual intervention.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">RETAIL / AI PERSONALIZATION</div>
            <h3>Decisions made per shopper, at a scale no team could do by hand.</h3>
            <p>The system reads browsing and purchase behavior in real time and decides what to recommend next — integrated into the existing platform with no disruption to how the business already runs.</p>
            <button className="ea-text-link" onClick={openContactModal}>Discuss a similar project</button>
          </div>
          <div className="ea-results">
            {CASE_RESULTS.map((r) => (
              <div key={r.a}><b>{r.a}</b><span>{r.b}</span></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* INDUSTRIES */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Where it works</div>
            <h2>Built around the<br />decision, not the demo.</h2>
          </div>
          <p>We design for environments where speed and accountability both matter, at the same time.</p>
        </Reveal>
        <div className="aa-pill-grid">
          {INDUSTRIES.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="aa-pill" key={item.a}>
              <b>{item.a}</b>
              <span>{item.b}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Agentic AI development FAQs</h2>
            <p>Short answers to the questions we are usually asked before an engagement begins.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div>
            <h2 id="contact-heading">Bring us the decision that has real stakes.</h2>
            <p>We'll help you frame the opportunity, identify the constraints and decide what a useful first pilot should look like.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Build with more certainty.</h3>
            <p>Tell us what your team needs to decide or execute faster.</p>

            <form onSubmit={handleContactSubmit} aria-label="Agentic AI development project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="agentic-fullname">Full Name</label>
                <input
                  id="agentic-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "agentic-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="agentic-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="agentic-phone">Phone Number (optional)</label>
                  <input
                    id="agentic-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "agentic-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="agentic-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="agentic-email">Email</label>
                  <input
                    id="agentic-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "agentic-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="agentic-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="agentic-subject">Subject</label>
                <input
                  id="agentic-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., An autonomous refund-approval agent"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "agentic-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="agentic-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="agentic-description">Project Details</label>
                <textarea
                  id="agentic-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "agentic-description-error" : undefined}
                />
                {contactErrors.description && <p id="agentic-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default AgenticAIDevelopment;
